import { useEffect, useId, useState } from 'react'
import { ImageIcon, UploadIcon } from 'lucide-react'
import { FormError } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/avif'
const MAX_BYTES = 10 * 1024 * 1024

/** Checks a chosen file before upload; returns an error message or null. */
export function imageFileError(f: File): string | null {
  if (!IMAGE_ACCEPT.split(',').includes(f.type)) return 'Choose a PNG, JPEG, GIF, WebP or AVIF image.'
  if (f.size > MAX_BYTES) return 'Images can be up to 10 MB.'
  return null
}

/**
 * Upload dialog for an image in the article (with a caption) or the featured image. The parent
 * uploads and saves in `onSubmit`, which returns an error message or null.
 */
export function ImageDialog({
  open,
  title,
  description,
  withCaption,
  currentSrc,
  currentAlt = '',
  onClose,
  onSubmit,
}: {
  open: boolean
  title: string
  description: string
  withCaption: boolean
  currentSrc?: string | null
  currentAlt?: string
  onClose: () => void
  onSubmit: (file: File | null, alt: string, caption: string) => Promise<string | null>
}) {
  const id = useId()
  const [file, setFile] = useState<File | null>(null)
  const [alt, setAlt] = useState('')
  const [caption, setCaption] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFile(null)
    setAlt(currentAlt)
    setCaption('')
    setError(null)
  }, [open, currentAlt])

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file && !currentSrc) return setError('Choose an image first.')
    setBusy(true)
    setError(null)
    const err = await onSubmit(file, alt.trim(), caption.trim())
    setBusy(false)
    if (err) setError(err)
    else onClose()
  }

  const src = preview ?? currentSrc
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/40">
            {src ? <img src={src} alt="" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="size-8 text-muted-foreground" aria-hidden />}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${id}-f`}>{currentSrc ? 'New image' : 'Image'}</Label>
            <Input
              id={`${id}-f`}
              type="file"
              accept={IMAGE_ACCEPT}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                setError(f ? imageFileError(f) : null)
                setFile(f && !imageFileError(f) ? f : null)
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${id}-a`}>Description (alt text)</Label>
            <Input id={`${id}-a`} value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={1000} aria-describedby={`${id}-ah`} />
            <p id={`${id}-ah`} className="text-xs text-muted-foreground">
              Read aloud to people who cannot see the image. Search engines use it too.
            </p>
          </div>
          {withCaption && (
            <div className="flex flex-col gap-2">
              <Label htmlFor={`${id}-c`}>Caption (optional)</Label>
              <Input id={`${id}-c`} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={1000} />
            </div>
          )}
          <FormError message={error} />
          <DialogFooter>
            <DialogClose render={<Button variant="outline" type="button" disabled={busy} />}>Cancel</DialogClose>
            <Button type="submit" disabled={busy}>
              {busy ? <Spinner /> : <UploadIcon aria-hidden />}
              {currentSrc && !file ? 'Save' : 'Upload image'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
